/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import React from "react";
import { FallbackProps } from "react-error-boundary";
import { CircularProgress, Collapse, Typography } from "@mui/material";

import { ContainedButton, TextButton } from "./buttons";
import { TransparentCard } from "./card";
import ScrollingBg from "./scrolling-bg";
import { useAppDispatch } from "../store/hooks";
import { clearCache } from "../store/slices/data";
import { logout } from "../store/slices/userData";
import { resetSettings } from "../store/slices/settings";
import { clearFirstTimeData } from "../store/slices/firstTimeData";

export function ErrorPage(props: FallbackProps): React.ReactNode {
  const [showError, setShowError] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();
  const error = `${props.error}`;

  function reload(): void {
    dispatch(resetSettings());
    dispatch(clearFirstTimeData());
    dispatch(logout());
    dispatch(clearCache());
    props.resetErrorBoundary();
  }

  return (
    <ScrollingBg>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <TransparentCard
          style={{
            width: "80%",
            maxWidth: 500,
            margin: 10,
          }}
        >
          <Typography
            style={{ fontSize: 18, fontWeight: "bold", marginBottom: 20 }}
          >
            PAL3 Update and Reload
          </Typography>
          <div className="column center" style={{ marginBottom: 20 }}>
            <TextButton onClick={() => setShowError(!showError)}>
              {showError ? "Hide details" : "Show details"}
            </TextButton>
            <Collapse in={showError}>
              <Typography color="error">{error}</Typography>
            </Collapse>
          </div>
          <Typography style={{ marginBottom: 20 }} textAlign="center">
            If you see this message while studying in PAL3 or you see it again
            after Reloading, please contact{" "}
            <a
              href={`mailto:pal3-support@ict.usc.edu?subject=CrashReport&body=${error}`}
            >
              pal3-support@ict.usc.edu
            </a>{" "}
            for support.
          </Typography>
          <ContainedButton onClick={reload}>Reload App</ContainedButton>
        </TransparentCard>
      </div>
    </ScrollingBg>
  );
}

export default function LoadingPage(props: {
  isLoading: boolean;
  children: JSX.Element;
  error?: boolean;
  message?: string;
  reload?: () => void;
}): JSX.Element {
  if (props.isLoading) {
    return (
      <ScrollingBg>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <TransparentCard
            style={{
              width: "80%",
              maxWidth: 500,
              margin: 10,
            }}
          >
            <Typography
              style={{ fontSize: 16, fontWeight: "bold", marginBottom: 20 }}
            >
              Loading
            </Typography>
            <CircularProgress size="4rem" />
            <Typography
              style={{
                marginTop: 20,
                marginBottom: 20,
                whiteSpace: "pre-line",
              }}
            >
              {props.message}
            </Typography>

            {props.reload && props.error && (
              <ContainedButton
                aria="reload content button"
                onClick={props.reload}
              >
                Reload
              </ContainedButton>
            )}
          </TransparentCard>
        </div>
      </ScrollingBg>
    );
  } else {
    return props.children;
  }
}
